import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';

import GameDetails from '../../components/ManageGame/GameDetails';
import PlayerList from '../../components/ManageGame/PlayerList';
import GameStats from '../../components/ManageGame/GameStats';
import GameSettlement from '../../components/ManageGame/GameSettlement';
import LoadingOverlay from '../../components/LoadingOverlay/LoadingOverlay';
import { useAlertDialog } from '../../components/ConfirmDialog/ConfirmDialogProvider';

import {
  fetchGame,
  setPhase,
  startGameSession,
  syncTimer,
  clearGameSession,
} from '../../store/slices/gameSlice';
import {
  clearStoredAdminSession,
  isManagementAdminRole,
  runAdminAuthedRequest,
} from '../../utils/adminAuth';

import './ManageGame.css';

const ManageGame = ({ adminRole }) => {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const showAlert = useAlertDialog();

  const { game, phase, loading, startedAt, durationSec, error } = useSelector(
    (state) => state.game
  );
  const [isSettling, setIsSettling] = React.useState(false);

  const handleAuthFailure = () => {
    clearStoredAdminSession();
    window.location.assign('/login');
  };

  useEffect(() => {
    dispatch(fetchGame(gameId, { onAuthFailure: handleAuthFailure }));
  }, [dispatch, gameId]);

  useEffect(() => {
    if (!game?.status) return;

    if (game.status === 'finished' && phase < 4) {
      dispatch(setPhase(4));
    }
  }, [dispatch, game?.status, phase]);

  // ✅ keep timer accurate while this page is open
  useEffect(() => {
    if (phase !== 3 || !startedAt || !durationSec) return;

    dispatch(syncTimer(Date.now()));
    const interval = setInterval(() => dispatch(syncTimer(Date.now())), 1000);
    return () => clearInterval(interval);
  }, [dispatch, phase, startedAt, durationSec]);

  const canModifyGame = Boolean(
    game?.status === 'upcoming' && adminRole && isManagementAdminRole(adminRole)
  );

  if (loading) return <LoadingOverlay label="Loading game" />;
  if (error) return <div>{error}</div>;
  if (!game) return <LoadingOverlay label="Loading game" />;

  const handleStart = () => {
    // Phase flow:
    // phase 1 -> 2 (setup)
    // phase 2 -> 3 (actual match starts)
    if (phase === 2) {
      dispatch(startGameSession()); // sets startedAt once + phase=3
    } else {
      dispatch(setPhase(phase + 1));
    }
  };

  const handleFinish = () => {
    dispatch(setPhase(4));
  };

  const handleSettle = async () => {
    if (isSettling) return;

    try {
      setIsSettling(true);
      const response = await runAdminAuthedRequest(
        (idToken) =>
          axios.put(
            `${process.env.REACT_APP_API_URL}/games/${gameId}/status`,
            { status: 'settled' },
            {
              headers: {
                Authorization: `Bearer ${idToken}`,
                'Content-Type': 'application/json',
              },
            }
          ),
        {
          onAuthFailure: handleAuthFailure,
        }
      );
      if (!response) return;

      await showAlert({
        title: 'SETTLEMENT',
        message: 'Game settled successfully.',
      });
      dispatch(clearGameSession());
      navigate('/');
    } catch (error) {
      const msg = error?.response?.data?.error || 'Failed to settle game.';
      await showAlert({
        title: 'SETTLEMENT',
        message: msg,
      });
      console.error('Error settling game:', error);
    } finally {
      setIsSettling(false);
    }
  };

  const handleModify = async () => {
    if (!game) return;
    navigate(`/edit-game/${gameId}`);
  };

  return (
    <div className="manage-game-container">
      {(loading || isSettling) && (
        <LoadingOverlay label={isSettling ? 'Settling game' : 'Loading game'} />
      )}
      {phase === 1 && (
        <>
          <GameDetails
            game={game}
            onStart={handleStart}
            onModify={handleModify}
            canEdit={canModifyGame}
          />
          <PlayerList
            onAuthFailure={handleAuthFailure}
            runAuthedRequest={runAdminAuthedRequest}
            allowRegistrationCancel={game?.status === 'upcoming'}
          />
        </>
      )}

      {phase === 2 && (
        <PlayerList
          onStart={handleStart}
          onAuthFailure={handleAuthFailure}
          runAuthedRequest={runAdminAuthedRequest}
        />
      )}

      {phase === 3 && (
        <GameStats
          onFinish={handleFinish}
          onAuthFailure={handleAuthFailure}
          runAuthedRequest={runAdminAuthedRequest}
        />
      )}
      {phase === 4 && (
        <GameSettlement
          onSettle={handleSettle}
          isSettling={isSettling}
          onAuthFailure={handleAuthFailure}
          runAuthedRequest={runAdminAuthedRequest}
        />
      )}
    </div>
  );
};

export default ManageGame;
